import React from 'react';
import { Quote, Star, ExternalLink } from 'lucide-react';
import { testimonials, googleMapsReviewsUrl } from '../data/mock';
import { useParallaxSimple } from '../hooks/useParallax';

export const TestimonialsSection = () => {
  const parallaxGlow = useParallaxSimple(0.065);

  return (
    <section
      id="depoimentos"
      className="py-16 sm:py-20 lg:py-24 bg-white border-t border-slate-100 relative overflow-hidden"
    >
      <div
        className="absolute left-1/2 top-0 w-[min(92vw,840px)] h-48 -translate-x-1/2 rounded-full bg-[#d4af37]/[0.08] blur-3xl pointer-events-none"
        style={parallaxGlow.style}
        aria-hidden
      />

      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-14">
          <span className="inline-block text-[#b8962e] text-xs sm:text-sm font-semibold tracking-widest uppercase mb-3 sm:mb-4">
            Depoimentos
          </span>
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl text-[#0f1f3d] leading-tight mb-4 tracking-tight">
            O que dizem nossos clientes
          </h2>
          <p className="text-slate-600 leading-relaxed text-sm sm:text-base">
            Relatos reais de quem confiou no nosso atendimento. Você também pode ver e deixar sua avaliação no Google Maps.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 lg:gap-8">
          {testimonials.map((testimonial) => (
            <article
              key={testimonial.id}
              className="group relative flex flex-col bg-[#faf9f7] p-6 sm:p-8 rounded-xl border border-slate-200/80 hover:border-[#d4af37]/35 transition-colors duration-300"
            >
              <div className="absolute -top-3.5 left-7">
                <div className="w-9 h-9 bg-[#d4af37] rounded-full flex items-center justify-center shadow-sm">
                  <Quote className="w-4 h-4 text-[#1a1a1a]" />
                </div>
              </div>

              <div className="flex gap-0.5 pt-3 mb-4" aria-label={`${testimonial.rating} de 5 estrelas`}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className={`w-4 h-4 ${
                      i < (testimonial.rating || 5)
                        ? 'fill-[#d4af37] text-[#d4af37]'
                        : 'text-slate-300'
                    }`}
                  />
                ))}
              </div>

              <p className="text-slate-600 italic leading-relaxed text-sm sm:text-base mb-6 flex-1">
                &ldquo;{testimonial.text}&rdquo;
              </p>

              <div className="flex items-center justify-between gap-3 border-t border-slate-200/80 pt-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center border border-[#d4af37]/25 shrink-0">
                    <span className="font-serif text-base font-semibold text-[#b8962e]">
                      {testimonial.name.charAt(0)}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-[#0f1f3d] text-sm truncate">{testimonial.name}</p>
                    <p className="text-xs text-slate-500 truncate">{testimonial.role}</p>
                  </div>
                </div>

                {testimonial.mapsUrl ? (
                  <a
                    href={testimonial.mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-medium text-[#b8962e] hover:text-[#8a6a14] whitespace-nowrap shrink-0"
                  >
                    No Maps
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                ) : null}
              </div>
            </article>
          ))}
        </div>

        <div className="mt-10 sm:mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href={googleMapsReviewsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-[#0f1f3d] hover:bg-[#1a2b48] text-white rounded-md px-6 py-3 text-sm font-semibold transition-colors"
          >
            Ver avaliações no Google Maps
            <ExternalLink className="w-4 h-4" />
          </a>
          <a
            href={googleMapsReviewsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 border border-[#d4af37] text-[#0f1f3d] hover:bg-[#d4af37]/10 rounded-md px-6 py-3 text-sm font-semibold transition-colors"
          >
            Deixar um comentário
          </a>
        </div>

        <p className="text-center text-xs text-slate-400 mt-8 max-w-2xl mx-auto">
          Depoimentos de clientes. Por questões de sigilo profissional, os nomes podem ser representados por iniciais.
          Os links direcionam às avaliações públicas no Google Maps.
        </p>
      </div>
    </section>
  );
};
